import httpService from '@Utils/httpService';

const baseUrl = 'api/master-imports/api-sources';

export default {
  /**
   * List API data sources for a master batch
   * @param {Object} params - { batchId, page, itemsPerPage }
   */
  async getDataSources(params) {
    const response = await httpService.get({
      url: baseUrl,
      params,
    });
    return response.data;
  },

  /**
   * Get a single API data source by ID
   * @param {string} id - Data source ID
   */
  async getDataSource(id) {
    const response = await httpService.get({
      url: `${baseUrl}/${id}`,
    });
    return response.data;
  },

  /**
   * Create a new API data source for a master batch
   * @param {Object} data - { batchId, name, connectionConfig, mapping, schedule, triggerImmediate }
   */
  async createDataSource(data) {
    const response = await httpService.post({
      url: baseUrl,
      data,
    });
    return response.data;
  },

  /**
   * Update an existing API data source
   * @param {string} id - Data source ID
   * @param {Object} data - Updated data source config
   */
  async updateDataSource(id, data) {
    const response = await httpService.put({
      url: `${baseUrl}/${id}`,
      data,
    });
    return response.data;
  },

  /**
   * Delete an API data source (also removes scheduled job)
   * @param {string} id - Data source ID
   */
  async deleteDataSource(id) {
    const response = await httpService.delete({
      url: `${baseUrl}/${id}`,
    });
    return response.data;
  },

  /**
   * Test connection to an external API
   * @param {Object} connectionConfig - { url, httpMethod, headers, auth, responseDataPath, pagination }
   * @returns {Promise<{success, statusCode, responseTimeMs, responseSizeBytes, errorMessage}>}
   */
  async testConnection(connectionConfig) {
    const response = await httpService.post({
      url: `${baseUrl}/test-connection`,
      data: { connectionConfig },
    });
    return response.data;
  },

  /**
   * Fetch preview data from an external API (first page, max 20 rows)
   * @param {Object} connectionConfig
   * @param {number} maxRows - Maximum rows to preview (default 20)
   * @returns {Promise<{success, headers, data, totalRecords, errorMessage}>}
   */
  async fetchPreview(connectionConfig, maxRows = 20, { signal } = {}) {
    const response = await httpService.post({
      url: `${baseUrl}/preview`,
      data: { connectionConfig, maxRows },
      signal,
    });
    return response.data;
  },

  /**
   * Trigger a manual data pull for a data source
   * @param {string} id - Data source ID
   */
  async triggerManualPull(id) {
    const response = await httpService.post({
      url: `${baseUrl}/${id}/trigger`,
    });
    return response.data;
  },

  /**
   * Toggle schedule on/off for a data source
   * @param {string} id - Data source ID
   * @param {boolean} enabled
   */
  async toggleSchedule(id, enabled) {
    const response = await httpService.put({
      url: `${baseUrl}/${id}/schedule-toggle`,
      data: { enabled },
    });
    return response.data;
  },

  /**
   * Get execution history for a data source
   * @param {string} id - Data source ID
   * @param {Object} params - { page, itemsPerPage }
   */
  async getExecutionHistory(id, params) {
    const response = await httpService.get({
      url: `${baseUrl}/${id}/history`,
      params,
    });
    return response.data;
  },
};
